
import React from 'react';
import { 
  FiPlay, FiPause, FiMic, FiMicOff, FiVideo, FiVideoOff, 
  FiEdit3, FiStopCircle, FiLoader 
} from 'react-icons/fi';
import { MdOutlineScreenShare, MdStopScreenShare } from 'react-icons/md';
import { BsFillRecordFill } from 'react-icons/bs';

const BottomControlsGamer = ({
  roomState, startStreaming, pauseStreaming, resumeStreaming, isInitializing, isRecordingLoading, isRecordingStopping,
  audioEnabled, toggleAudio, videoEnabled, toggleVideo, showWhiteboard, handleCloseWhiteboard, handleOpenWhiteboard,
  activeScreenShare, stopScreenShare, handleStreamerScreenShareClick, isRecording, screenCaptureActive, handleRecordingToggle,
  formatRecordingTime, recordingTimer, endStreaming
}) => {
  
  // Helper for tooltip
  const Tooltip = ({ text }) => (
    <span className="absolute -top-10 px-3 py-1.5 bg-gray-900 text-white text-xs font-bold rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap shadow-xl border border-gray-700 z-50">
      {text}
    </span>
  );

  return (
    <div className="w-full bg-[#0d0e14]/90 backdrop-blur-xl border-t border-gray-800 py-3 z-40 flex flex-wrap items-center justify-center gap-4">
      
      {/* ── Main Stream Controls (Left Island) ── */}
      <div className="flex items-center gap-2 bg-[#1a1b26] p-2 rounded-full border border-gray-700 shadow-xl">
        {!roomState.isStreaming ? (
          <button onClick={startStreaming} disabled={isInitializing || isRecordingLoading}
            className="group relative flex items-center justify-center w-12 h-12 bg-emerald-500 hover:bg-emerald-400 text-white rounded-full transition-transform hover:scale-110 shadow-[0_0_15px_rgba(16,185,129,0.4)] disabled:opacity-50 disabled:hover:scale-100">
            <FiPlay className="text-xl ml-1" />
            <Tooltip text="Start Stream" />
          </button>
        ) : roomState.isPaused ? (
          <button onClick={resumeStreaming} disabled={isRecordingLoading}
            className="group relative flex items-center justify-center w-12 h-12 bg-blue-500 hover:bg-blue-400 text-white rounded-full transition-transform hover:scale-110 shadow-[0_0_15px_rgba(59,130,246,0.4)] disabled:opacity-50 disabled:hover:scale-100">
            <FiPlay className="text-xl ml-1" />
            <Tooltip text="Resume Stream" />
          </button>
        ) : (
          <button onClick={pauseStreaming} disabled={isRecordingLoading}
            className="group relative flex items-center justify-center w-12 h-12 bg-amber-500 hover:bg-amber-400 text-white rounded-full transition-transform hover:scale-110 shadow-[0_0_15px_rgba(245,158,11,0.4)] disabled:opacity-50 disabled:hover:scale-100">
            <FiPause className="text-xl" />
            <Tooltip text="Pause Stream" />
          </button>
        )}
      </div>

      {/* ── Media Controls (Center Island) ── */}
      <div className="flex items-center gap-2 bg-[#1a1b26] p-2 rounded-full border border-gray-700 shadow-xl">
        
        <button onClick={toggleAudio} disabled={isRecordingLoading || isRecordingStopping}
          className={`group relative flex items-center justify-center w-12 h-12 rounded-full transition-all hover:scale-110 disabled:opacity-50 disabled:hover:scale-100 ${
            audioEnabled ? 'bg-gray-800 text-gray-200 hover:bg-gray-700' : 'bg-red-500 text-white shadow-[0_0_15px_rgba(239,68,68,0.5)]'
          }`}>
          {audioEnabled ? <FiMic className="text-xl" /> : <FiMicOff className="text-xl" />}
          <Tooltip text={audioEnabled ? "Mute Mic" : "Unmute Mic"} />
        </button>

        <button onClick={toggleVideo} disabled={isInitializing || isRecordingLoading || isRecordingStopping}
          className={`group relative flex items-center justify-center w-12 h-12 rounded-full transition-all hover:scale-110 disabled:opacity-50 disabled:hover:scale-100 ${
            videoEnabled ? 'bg-gray-800 text-gray-200 hover:bg-gray-700' : 'bg-red-500 text-white shadow-[0_0_15px_rgba(239,68,68,0.5)]'
          }`}>
          {videoEnabled ? <FiVideo className="text-xl" /> : <FiVideoOff className="text-xl" />}
          <Tooltip text={videoEnabled ? "Turn Off Video" : "Turn On Video"} />
        </button>

        <div className="w-px h-8 bg-gray-700 mx-1"></div>

        <button onClick={(e) => { e.stopPropagation(); showWhiteboard ? handleCloseWhiteboard() : handleOpenWhiteboard(); }}
          className={`group relative flex items-center justify-center w-12 h-12 rounded-full transition-all hover:scale-110 ${
            showWhiteboard ? 'bg-indigo-500 text-white shadow-[0_0_15px_rgba(99,102,241,0.5)]' : 'bg-gray-800 text-gray-200 hover:bg-gray-700'
          }`}>
          <FiEdit3 className="text-xl" />
          <Tooltip text={showWhiteboard ? "Close Whiteboard" : "Open Whiteboard"} />
        </button>

        {/* 👇 YAHAN CHANGE KIYA HAI 👇 */}
        {activeScreenShare?.source === "streamer" ? (
          <button onClick={stopScreenShare} disabled={isRecordingLoading || isRecordingStopping}
            className="group relative flex items-center justify-center w-12 h-12 bg-rose-600 text-white rounded-full transition-all hover:scale-110 shadow-[0_0_15px_rgba(225,29,72,0.5)] disabled:opacity-50 disabled:hover:scale-100">
            <MdStopScreenShare className="text-xl" />
            <Tooltip text="Stop Screen Share" />
          </button>
        ) : (
          <button onClick={handleStreamerScreenShareClick} disabled={isInitializing || isRecordingLoading || isRecordingStopping}
            className="group relative flex items-center justify-center w-12 h-12 bg-gray-800 text-gray-200 hover:bg-gray-700 rounded-full transition-all hover:scale-110 disabled:opacity-50 disabled:hover:scale-100">
            <MdOutlineScreenShare className="text-xl" />
            <Tooltip text="Share Screen" />
          </button>
        )}
      </div>

      {/* ── Record & End (Right Island) ── */}
      <div className="flex items-center gap-2 bg-[#1a1b26] p-2 rounded-full border border-gray-700 shadow-xl">
        
        <button onClick={handleRecordingToggle} disabled={isRecordingLoading || isRecordingStopping || isInitializing}
          className={`group relative flex items-center justify-center rounded-full transition-all hover:scale-110 disabled:opacity-50 disabled:hover:scale-100 ${
            isRecording 
              ? 'px-4 h-12 bg-red-600 text-white animate-pulse shadow-[0_0_15px_rgba(220,38,38,0.6)]' 
              : 'w-12 h-12 bg-gray-800 text-gray-200 hover:bg-gray-700'
          }`}>
          {isRecordingLoading || isRecordingStopping ? (
            <FiLoader className="text-xl animate-spin" />
          ) : isRecording ? (
            <div className="flex items-center gap-2">
              <BsFillRecordFill className="text-sm" />
              <span className="font-mono text-sm font-bold tracking-wider">{formatRecordingTime(recordingTimer)}</span>
            </div>
          ) : (
            <BsFillRecordFill className="text-xl text-red-400" />
          )}
          <Tooltip text={isRecording ? "Stop Recording" : "Start Recording"} />
        </button>

        {roomState.isStreaming && (
          <>
            <div className="w-px h-8 bg-gray-700 mx-1"></div>
            <button onClick={endStreaming} disabled={isRecordingLoading || isRecordingStopping}
              className="group relative flex items-center justify-center w-12 h-12 bg-red-600/20 text-red-500 hover:bg-red-600 hover:text-white rounded-full transition-all hover:scale-110 border border-red-500/50 hover:border-transparent disabled:opacity-50 disabled:hover:scale-100">
              <FiStopCircle className="text-xl" />
              <Tooltip text="End Stream" />
            </button>
          </>
        )}

      </div>
    </div>
  );
};

export default BottomControlsGamer;